import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { sendLanguageWithRetry } from "../apis/langSync";
import { UserContext } from "../contexts/UserContext";
import "./LanguageSwitcher.css";

const LANGS = ["en", "fr", "nl"];

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const { userToken } = useContext(UserContext) || {};
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const active = LANGS.includes(i18n.language) ? i18n.language : "en";

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const change = (lng) => {
    setOpen(false);
    if (lng === active) return;
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem("izzygo_lang", lng);
    } catch (e) {
      // localStorage unavailable (private mode / SSR) — in-memory change still applies.
    }
    // Persist server-side for localized notifications (retry + pending-flag; UI already swapped)
    if (userToken) {
      sendLanguageWithRetry(userToken, lng);
    }
  };

  return (
    <div className="izzy-lang" ref={rootRef}>
      <button
        type="button"
        className={`izzy-lang__trigger${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="izzy-lang__code">{active.toUpperCase()}</span>
        <svg className="izzy-lang__chev" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul className="izzy-lang__menu" role="listbox" aria-label="Available languages">
          {LANGS.map((lng) => (
            <li key={lng} role="option" aria-selected={lng === active}>
              <button
                type="button"
                className={`izzy-lang__item${lng === active ? " is-active" : ""}`}
                onClick={() => change(lng)}
              >
                <span className="izzy-lang__dot" aria-hidden="true" />
                {lng.toUpperCase()}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
